import { PROVIDER_IMPORT_FIELDS, ProviderImportField } from "./providerImportFields";
import {
    ColumnSuggestion as GenericColumnSuggestion,
    MappingConfidence,
    createMappingSuggester,
    normalizeHeader,
    sampleMatches,
} from "./core/suggestMapping";

export { normalizeHeader };
export type { MappingConfidence };

export type ColumnSuggestion = GenericColumnSuggestion & {
    field: ProviderImportField["name"] | null;
};

/**
 * Sinonimos por atributo. Es una ayuda para adivinar mejor, NO una lista de encabezados
 * requeridos: los archivos vienen de sistemas externos y las columnas pueden llamarse
 * como sea. Ampliar esta tabla con cada archivo real que aparezca es barato y seguro.
 */
const FIELD_SYNONYMS: Record<string, string[]> = {
    nombre: [
        "nombre",
        "proveedor",
        "razon social",
        "nombre proveedor",
        "descripcion",
        "suplidor",
    ],
    nombre_comercial: ["nombre comercial", "comercial", "nombre negocio", "nombre del negocio", "razon comercial", "alias", "fantasia", "nombre fantasia"],
    codigo: ["codigo", "cod", "clave", "numero proveedor", "no proveedor", "id proveedor", "referencia"],
    identificacion: [
        "identificacion",
        "cedula",
        "ced",
        "cedula juridica",
        "ced juridica",
        "cedula fisica",
        "id",
        "nit",
        "ruc",
        "rfc",
        "dni",
        "documento",
        "cif",
    ],
    email: ["email", "correo", "correo electronico", "e mail", "mail", "correos"],
    telefono1: ["telefono", "tel", "telefono 1", "celular", "movil", "contacto telefonico", "numero telefonico"],
    telefono2: ["telefono 2", "tel 2", "telefono alterno", "celular 2", "otro telefono", "fax"],
    telefono1_cd: ["codigo pais", "codigo de pais", "cod pais", "prefijo", "lada"],
    telefono2_cd: ["codigo pais 2", "codigo de pais 2", "prefijo 2"],
    direccion: ["direccion", "domicilio", "senas", "ubicacion", "direccion exacta"],
    plazo: ["plazo", "plazo de credito", "dias credito", "dias de credito", "termino de pago", "condicion de pago"],
    contacto: ["contacto", "nombre de contacto", "persona contacto", "encargado", "agente", "ejecutivo"],
    tipo: ["tipo", "tipo proveedor", "credito contado", "condicion", "modalidad", "forma de pago"],
    cuentas_bancarias: ["cuentas bancarias", "cuenta bancaria", "cuenta", "banco", "iban", "sinpe", "sinpe movil"],
};

const DIGITS_ONLY = /^\d+$/;

/**
 * Deduce el atributo mirando los valores de la columna.
 *
 * Es la unica senal disponible cuando el archivo no trae encabezados, y sirve de
 * confirmacion cuando si los trae. A diferencia de clientes no se detecta booleano ni
 * codigo de actividad: `Provider` no tiene esos atributos.
 */
const detectByContent = (sample: string[]): ProviderImportField["name"] | null => {
    const matches = (predicate: (value: string) => boolean): boolean => sampleMatches(sample, predicate);

    if (matches((value) => value.includes("@") && value.includes("."))) return "email";

    const digits = (value: string): string => value.replace(/[\s-]/g, "");

    if (matches((value) => DIGITS_ONLY.test(digits(value)) && digits(value).length === 8)) return "telefono1";

    if (
        matches((value) => {
            const clean = digits(value);
            return DIGITS_ONLY.test(clean) && clean.length >= 9 && clean.length <= 12;
        })
    ) {
        return "identificacion";
    }

    return null;
};

const suggester = createMappingSuggester({
    fields: PROVIDER_IMPORT_FIELDS,
    synonyms: FIELD_SYNONYMS,
    detectByContent,
});

/** Mejor atributo para un nombre de columna, o `null` si ninguno destaca lo suficiente. */
export const suggestFieldByName = suggester.suggestFieldByName as (
    header: string
) => ProviderImportField["name"] | null;

export const suggestFieldByContent = suggester.suggestFieldByContent as (
    values: string[]
) => ProviderImportField["name"] | null;

/** Propone un mapeo columna -> atributo combinando el nombre de la columna y su contenido. */
export const suggestColumnMapping = suggester.suggestColumnSuggestions as (
    columnNames: string[],
    dataRows: string[][]
) => ColumnSuggestion[];

export const suggestProviderColumnMapping = suggester.suggestColumnMapping;
